<script setup lang="ts">
/**
 * RegisterStep2Address — verification method picker (step 2).
 * Mirrors resident-register.html STEP 2 panel exactly.
 * The "Request verification" button is disabled until a method is selected.
 */
const emit = defineEmits<{
  (e: 'next', method: 'MailedCode' | 'DocumentUpload' | 'NeighborVouch'): void;
  (e: 'back'): void;
}>();

import { ref } from 'vue';

type Method = 'MailedCode' | 'DocumentUpload' | 'NeighborVouch' | null;
const selectedMethod = ref<Method>(null);

function onNext() {
  if (!selectedMethod.value) return;
  emit('next', selectedMethod.value);
}
</script>

<template>
  <div data-step="2" data-testid="register-step-2">
    <div class="reg-head">
      <h1 class="reg-title">Verify your residency</h1>
      <p class="reg-sub">
        Because the resident portal includes private community updates, the board verifies
        every account before approving access. Pick how you'd like to verify.
      </p>
    </div>

    <div class="reg-body stack gap-12">
      <!-- Mailed verification code -->
      <label
        class="verify-card"
        :class="{ selected: selectedMethod === 'MailedCode' }"
        data-testid="verify-mailed-code"
      >
        <input
          v-model="selectedMethod"
          type="radio"
          name="verify"
          value="MailedCode"
          data-testid="verify-input-mailed"
        >
        <div class="vc-ic">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" />
          </svg>
        </div>
        <div class="vc-body">
          <div class="vc-name">
            Mailed verification code
            <span class="badge badge-info no-dot" style="font-size: 10px; height: 16px;">Recommended</span>
          </div>
          <div class="vc-desc">We mail a 6-digit code to your address. You enter it here to confirm residency. The board still does a final review.</div>
        </div>
        <div class="vc-time font-mono tabular-nums">3–5 days</div>
      </label>

      <!-- Document upload -->
      <label
        class="verify-card"
        :class="{ selected: selectedMethod === 'DocumentUpload' }"
        data-testid="verify-doc-upload"
      >
        <input
          v-model="selectedMethod"
          type="radio"
          name="verify"
          value="DocumentUpload"
          data-testid="verify-input-doc"
        >
        <div class="vc-ic">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 14h8M8 18h6" />
          </svg>
        </div>
        <div class="vc-body">
          <div class="vc-name">Upload proof of residency</div>
          <div class="vc-desc">Upload a recent utility bill, mortgage statement, or rental agreement matching the address. The board reviews privately, then deletes the document.</div>
        </div>
        <div class="vc-time font-mono tabular-nums">~1 day</div>
      </label>

      <!-- Neighbor vouch -->
      <label
        class="verify-card"
        :class="{ selected: selectedMethod === 'NeighborVouch' }"
        data-testid="verify-neighbor-vouch"
      >
        <input
          v-model="selectedMethod"
          type="radio"
          name="verify"
          value="NeighborVouch"
          data-testid="verify-input-neighbor"
        >
        <div class="vc-ic">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 11h-6" /><path d="M19 8v6" />
          </svg>
        </div>
        <div class="vc-body">
          <div class="vc-name">Vouch from an approved neighbor</div>
          <div class="vc-desc">Ask a neighbor who already has a HOA Watch account to confirm you live here. They'll get a one-tap text. The board still reviews.</div>
        </div>
        <div class="vc-time font-mono tabular-nums">~hours</div>
      </label>

      <!-- Info note (not teal accent — just informational) -->
      <div style="background: var(--bg-soft); border: 1px solid var(--border); border-radius: var(--r-md, 8px); padding: 14px 16px; display: flex; gap: 12px; align-items: flex-start; margin-top: 4px;">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width: 18px; height: 18px; color: var(--muted); flex-shrink: 0; margin-top: 2px;">
          <circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" />
        </svg>
        <div style="font-size: 12.5px; color: var(--fg-soft); line-height: 1.5;">
          You can <strong>still report community issues anonymously</strong> via the
          <a href="/portal" style="color: var(--navy-700); font-weight: 600;">public portal</a>
          without an account. Verification only unlocks tracking your own submissions and private board updates.
        </div>
      </div>
    </div>

    <div class="reg-foot">
      <button class="btn btn-ghost" type="button" data-testid="reg-step2-back" @click="$emit('back')">
        &larr; Back
      </button>
      <button
        class="btn btn-primary"
        type="button"
        :disabled="!selectedMethod"
        data-action="next"
        data-testid="reg-step2-next"
        @click="onNext"
      >
        Request verification
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Verification cards — mirrors prototype */
.verify-card {
  border: 1px solid var(--border);
  border-radius: var(--r-md, 8px);
  padding: 16px;
  cursor: pointer;
  background: var(--surface);
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.verify-card:hover { border-color: var(--border-strong); }
.verify-card input { margin-top: 4px; accent-color: var(--navy-700); flex-shrink: 0; }
.verify-card.selected {
  background: var(--navy-50);
  border-color: var(--navy-700);
  box-shadow: 0 0 0 3px var(--navy-100);
}
.vc-ic {
  width: 38px;
  height: 38px;
  border-radius: var(--r-sm, 4px);
  background: var(--bg-soft);
  color: var(--navy-700);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}
.verify-card.selected .vc-ic { background: var(--surface); border: 1px solid var(--navy-100); }
.vc-ic svg { width: 18px; height: 18px; }
.vc-body { flex: 1; }
.vc-name { font-weight: 600; font-size: 14px; display: flex; align-items: center; gap: 8px; }
.vc-desc { font-size: 12.5px; color: var(--muted); margin-top: 3px; line-height: 1.5; }
.vc-time { margin-left: auto; font-size: 11.5px; color: var(--muted); }
</style>
